import { useState, useEffect } from "react";
import {
  FileText,
  Mail,
  Linkedin,
  Video,
  Newspaper,
  Layout,
  Share2,
  Sparkles,
  Copy,
  Check,
  RefreshCw,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  Calendar,
  CalendarPlus,
  ExternalLink,
  Archive
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hub`;
const API_PLANNING = `${process.env.REACT_APP_BACKEND_URL}/api/marketing/editorial`;

const ICONS = {
  "mail": Mail,
  "linkedin": Linkedin,
  "file-text": FileText,
  "video": Video,
  "layout": Layout,
  "share-2": Share2,
  "newspaper": Newspaper,
  "calendar": Calendar
};

const TONES = [
  { id: "professional", label: "Professionnel" },
  { id: "casual", label: "Decontracte" },
  { id: "formal", label: "Formel" },
  { id: "friendly", label: "Amical" },
  { id: "persuasive", label: "Persuasif" },
  { id: "informative", label: "Informatif" }
];

export default function ContentFactory() {
  const [contentTypes, setContentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("professional");
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  // État pour le dialog "Ajouter au Planning"
  const [planningDialogOpen, setPlanningDialogOpen] = useState(false);
  const [planningTitle, setPlanningTitle] = useState("");
  const [planningDate, setPlanningDate] = useState("");
  const [planningPriority, setPlanningPriority] = useState("medium");
  const [addingToPlan, setAddingToPlan] = useState(false);

  useEffect(() => {
    fetchContentTypes();
    fetchHistory();
  }, []);

  const fetchContentTypes = async () => {
    try {
      const response = await axios.get(`${API}/content/types`, {
        withCredentials: true
      });
      setContentTypes(response.data.types || []);
      if (response.data.types?.length > 0) {
        setSelectedType(response.data.types[0].id);
      }
    } catch (error) {
      console.error("Error fetching content types:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/content/history?limit=10`, {
        withCredentials: true
      });
      setHistory(response.data.contents || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const generateContent = async () => {
    if (!prompt.trim()) {
      toast.error("Veuillez saisir une description");
      return;
    }

    setGenerating(true);
    setGeneratedContent(null);

    try {
      const response = await axios.post(
        `${API}/content/generate`,
        {
          content_type: selectedType,
          prompt: prompt,
          tone: tone,
          language: "fr"
        },
        { withCredentials: true }
      );

      setGeneratedContent(response.data);
      fetchHistory();
      toast.success("Contenu genere avec succes!");
    } catch (error) {
      console.error("Error generating:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la generation");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (!generatedContent) return;
    try {
      await navigator.clipboard.writeText(generatedContent.generated_content);
      setCopied(true);
      toast.success("Copie dans le presse-papier!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Erreur lors de la copie");
    }
  };

  const sendFeedback = async (feedback) => {
    if (!generatedContent?.id) return;
    try {
      await axios.post(
        `${API}/content/${generatedContent.id}/feedback`,
        { feedback },
        { withCredentials: true }
      );
      toast.success(feedback === "positive" ? "Merci pour votre retour positif!" : "Merci, nous ameliorerons nos résultats");
    } catch (error) {
      console.error("Error sending feedback:", error);
    }
  };

  // Ouvrir le dialog pour ajouter au planning
  const openPlanningDialog = () => {
    if (!generatedContent?.id) {
      toast.error("Générez d'abord du contenu");
      return;
    }
    // Pré-remplir le titre avec le début du prompt
    const suggestedTitle = prompt.length > 50 ? prompt.substring(0, 50) + "..." : prompt;
    setPlanningTitle(suggestedTitle);
    setPlanningDate(new Date().toISOString().split('T')[0]);
    setPlanningDialogOpen(true);
  };

  // Ajouter le contenu au planning éditorial
  const addToPlanning = async () => {
    if (!generatedContent?.id || !planningTitle) {
      toast.error("Titre requis");
      return;
    }

    setAddingToPlan(true);
    try {
      const response = await axios.post(
        `${API_PLANNING}/from-content`,
        {
          content_id: generatedContent.id,
          title: planningTitle,
          target_date: planningDate || null,
          priority: planningPriority,
          notes: `Généré depuis Content Factory. Prompt: ${prompt}`
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Contenu ajouté au planning éditorial!");
        setPlanningDialogOpen(false);
        // Mettre à jour l'historique pour montrer le badge "Planifié"
        fetchHistory();
      }
    } catch (error) {
      console.error("Error adding to planning:", error);
      toast.error("Erreur lors de l'ajout au planning");
    } finally {
      setAddingToPlan(false);
    }
  };

  const loadFromHistory = (item) => {
    setSelectedType(item.content_type);
    setPrompt(item.prompt);
    setGeneratedContent({
      id: item.id,
      content_type: item.content_type,
      generated_content: item.generated_content,
      is_planned: item.is_planned
    });
  };

  const archiveContent = async (contentId, e) => {
    e.stopPropagation(); // Empêcher le clic sur l'item parent
    try {
      await axios.post(`${API}/content/${contentId}/archive`, {}, {
        withCredentials: true
      });
      toast.success("Contenu archivé");
      // Retirer l'item de l'historique local
      setHistory(prev => prev.filter(item => item.id !== contentId));
    } catch (error) {
      console.error("Error archiving content:", error);
      toast.error("Erreur lors de l'archivage");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeInfo = (typeId) => {
    return contentTypes.find(t => t.id === typeId) || { name: typeId, icon: "file-text" };
  };

  const getIcon = (iconName) => {
    return ICONS[iconName] || FileText;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            Content Factory
          </h1>
          <p className="text-zinc-400 mt-1">Generez du contenu marketing professionnel avec Claude</p>
        </div>
      </div>

      {/* Dialog Ajouter au Planning */}
      <Dialog open={planningDialogOpen} onOpenChange={setPlanningDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-purple-400" />
              Ajouter au Planning Éditorial
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Planifiez ce contenu pour publication ultérieure
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Titre du plan</label>
              <Input
                value={planningTitle}
                onChange={(e) => setPlanningTitle(e.target.value)}
                placeholder="Titre du contenu planifié"
                className="bg-zinc-800 border-white/10"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Date cible</label>
                <Input
                  type="date"
                  value={planningDate}
                  onChange={(e) => setPlanningDate(e.target.value)}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Priorité</label>
                <Select value={planningPriority} onValueChange={setPlanningPriority}>
                  <SelectTrigger className="bg-zinc-800 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPlanningDialogOpen(false)}
              className="border-white/20"
            >
              Annuler
            </Button>
            <Button
              onClick={addToPlanning}
              disabled={addingToPlan || !planningTitle}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {addingToPlan ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CalendarPlus className="w-4 h-4 mr-2" />
              )}
              Ajouter au Planning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Type de contenu</CardTitle>
              <CardDescription className="text-zinc-500">
                Selectionnez le format de contenu a générer
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {contentTypes.map(type => {
                  const Icon = getIcon(type.icon);
                  const isSelected = selectedType === type.id;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type.id)}
                      className={`p-4 rounded-xl border transition-all ${
                        isSelected
                          ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                          : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                      }`}
                    >
                      <Icon className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">{type.name}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-500 mb-2 block">Ton</label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      {TONES.map(t => (
                        <SelectItem key={t.id} value={t.id} className="text-white hover:bg-zinc-700">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm text-zinc-500 mb-2 block">
                  Decrivez le contenu souhaite
                </label>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="bg-zinc-800 border-white/10 text-white min-h-[120px]"
                  placeholder="Decrivez le contenu que vous souhaitez générer..."
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={generateContent}
                  disabled={generating || !prompt.trim()}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generation...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Générer
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {generatedContent && (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    {(() => {
                      const info = getTypeInfo(generatedContent.content_type);
                      const Icon = getIcon(info.icon);
                      return <Icon className="w-5 h-5 text-purple-400" />;
                    })()}
                    Contenu genere
                    <Badge variant="secondary" className="bg-purple-500/20 text-purple-400 ml-2">
                      Claude
                    </Badge>
                    {generatedContent.is_planned && (
                      <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-400 ml-2">
                        <Calendar className="w-3 h-3 mr-1" />
                        Planifié
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => sendFeedback("positive")}
                      className="text-zinc-400 hover:text-emerald-400"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => sendFeedback("negative")}
                      className="text-zinc-400 hover:text-red-400"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="border-white/10 text-zinc-300"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-emerald-400" />
                          Copie!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copier
                        </>
                      )}
                    </Button>
                    {/* Bouton Ajouter au Planning */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openPlanningDialog}
                      disabled={generatedContent.is_planned}
                      className="border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
                      data-testid="add-to-planning-btn"
                    >
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      {generatedContent.is_planned ? "Planifié" : "Planifier"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-lg bg-zinc-950 border border-white/5">
                  <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans">
                    {generatedContent.generated_content}
                  </pre>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <Card className="bg-zinc-900/50 border-white/10 h-fit">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />
              Historique
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucun contenu genere</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-2 pr-4">
                  {history.map((item, index) => {
                    const info = getTypeInfo(item.content_type);
                    const Icon = getIcon(info.icon);
                    return (
                      <div
                        key={item.id || index}
                        className="p-3 rounded-lg bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors cursor-pointer group relative"
                        onClick={() => loadFromHistory(item)}
                      >
                        {/* Bouton d'archivage au survol */}
                        <button
                          onClick={(e) => archiveContent(item.id, e)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 rounded-md hover:bg-zinc-500/20 text-zinc-500 hover:text-zinc-300 transition-all"
                          title="Archiver ce contenu"
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 text-purple-400" />
                          <Badge variant="secondary" className="bg-zinc-700 text-zinc-300 text-xs">
                            {info.name}
                          </Badge>
                          {item.is_planned && (
                            <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-400 text-xs">
                              <Calendar className="w-3 h-3 mr-1" />
                              Planifié
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400 line-clamp-2">
                          {item.prompt}
                        </p>
                        <p className="text-xs text-zinc-600 mt-1">
                          {formatDate(item.created_at)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
